import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Phone, Mail, MapPin, Clock, Send, Loader2, Banknote, Smartphone, Instagram } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${API}/business-info`);
        setBusinessInfo(response.data);
      } catch (error) {
        console.error('Error fetching business info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinessInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success('Thank you! We will contact you soon to confirm your appointment.');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit booking. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading || !businessInfo) {
    return (
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4 flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-800" />
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Book Your Appointment
          </h2>
          <p className="text-xl text-gray-600">
            Ready for beautiful braids? Get in touch with us today
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="border-2 border-gray-200 shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(555) 123-4567"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Desired Service
                  </label>
                  <Input
                    type="text"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    placeholder="e.g., Box Braids, Knotless Braids"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your hair length, preferred date, or any special requests..."
                    rows={4}
                    className="w-full"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-amber-800 hover:bg-amber-900 text-white text-lg py-6"
                  disabled={submitting}
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" /> Send Message</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="border-2 border-gray-200 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-800 text-white rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Phone</div>
                      <a href={`tel:${businessInfo.phone}`} className="text-amber-800 hover:text-amber-900">
                        {businessInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-800 text-white rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Email</div>
                      <a href={`mailto:${businessInfo.email}`} className="text-amber-800 hover:text-amber-900">
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-800 text-white rounded-full flex items-center justify-center">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Location</div>
                      <p className="text-gray-600">
                        {businessInfo.address}<br />
                        {businessInfo.city}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-800 text-white rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Hours</div>
                      <p className="text-gray-600 text-sm">
                        {businessInfo.hours_weekday}<br />
                        {businessInfo.hours_saturday}<br />
                        {businessInfo.hours_sunday}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card className="border-2 border-amber-200 shadow-lg bg-amber-50" data-testid="payment-methods-card">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Payment Methods
                </h3>
                <p className="text-gray-700 mb-4">
                  We accept the following payment methods after your appointment:
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 bg-white p-4 rounded-lg border border-amber-200" data-testid="payment-cash">
                    <div className="w-12 h-12 bg-amber-800 text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Banknote className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Cash</div>
                      <div className="text-xs text-gray-500">CHF</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 bg-white p-4 rounded-lg border border-amber-200" data-testid="payment-twint">
                    <div className="w-12 h-12 bg-amber-800 text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">TWINT</div>
                      <div className="text-xs text-gray-500">Mobile payment</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Follow Us on Instagram */}
            {businessInfo.instagram && (
              <a
                href={`https://www.instagram.com/${businessInfo.instagram.replace('@', '')}/`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="block"
              >
                <Card className="border-2 border-amber-200 shadow-lg bg-gradient-to-br from-amber-800 to-amber-900 text-white hover:shadow-2xl transition-all hover:scale-[1.02] cursor-pointer">
                  <CardContent className="p-8">
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Instagram className="w-7 h-7" />
                      </div>
                      <div>
                        <div className="text-sm text-amber-100 uppercase tracking-wider">Follow on Instagram</div>
                        <h3 className="text-2xl font-bold">
                          {businessInfo.instagram.startsWith('@') ? businessInfo.instagram : `@${businessInfo.instagram}`}
                        </h3>
                      </div>
                    </div>
                    <p className="text-amber-50 text-sm">
                      See our latest styles, behind-the-scenes moments, and client transformations.
                      DM us to book your appointment!
                    </p>
                  </CardContent>
                </Card>
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
